<script setup lang="ts">
  import { useMenuStore } from '@/composition/use-menu';
  import { computed, inject, nextTick, ref } from 'vue';
  import ModalPosNewOrderMenuItem from './ModalPosNewOrderMenuItem.vue';
  import { IItem } from '@/interfaces/IItem';
  import { useModal } from '@/composition/use-modal';
  import ModalPosNewOrderItem from '../item/ModalPosNewOrderItem.vue';
  import { ICartItem } from '@/interfaces/ICart';
  import { useOrdering } from '@/composition/use-ordering';
  import ModalPosNewOrderSummaryButton from '../ModalPosNewOrderSummaryButton.vue';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { trimString } from '@/utils/string.utils';

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const modals = useModal();
  const menuStore = useMenuStore();
  const orderHelper = inject<ReturnType<typeof useOrdering>>('orderHelper')!;
  const isMediumDevice = breakpoints.smallerOrEqual('lg');
  const isSmallDevice = breakpoints.smallerOrEqual('md');

  const search = ref<string | null>(null);

  const tabs = computed(() => {
    if (!menuStore.categoryBlocks) return [];

    return menuStore.categoryBlocks
      .filter((c) => c.id !== -1)
      .map((block) => {
        return {
          id: block.id,
          label: trimString(block.name, 20),
        };
      });
  });

  const selectedTabIndex = ref(0);
  const selectedTab = computed(() => tabs.value[selectedTabIndex.value]);

  const items = computed(() => {
    if (!menuStore.categoryBlocks) return [];

    const searchTerm = search.value?.trim?.()?.toLowerCase?.();

    if (!searchTerm) {
      return (
        menuStore.categoryBlocks.find((c) => c.id === selectedTab.value?.id)
          ?.items || []
      );
    }

    const allItems = menuStore.visibleItems;

    return (
      allItems?.filter((item) => {
        return (
          item.name.toLowerCase().includes(searchTerm) ||
          item.description?.toLowerCase?.()?.includes?.(searchTerm)
        );
      }) || []
    );
  });

  const hasCategories = computed(() => tabs.value.length > 0);
  const hasItems = computed(() => items.value.length > 0);
  const isSearching = computed(
    () => search.value !== null && search.value.trim() !== ''
  );

  function onItemClick(item: IItem) {
    if (!item.hasOptions) {
      orderHelper.addItem({
        item,
        itemId: item.id,
        quantity: 1,
        optionValues: {},
        unitPrice: item.discountedPrice ?? item.price,
        totalPrice: item.discountedPrice ?? item.price,
        userComment: '',
        index: 0,
      });
      return;
    }

    modals.show(
      // @ts-ignore
      ModalPosNewOrderItem,
      {
        item,
      },
      {
        name: 'pos-item',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 840,
        transition: 'none',
      },
      {
        save: async (item: ICartItem) => {
          orderHelper.addItem(item);
        },
      }
    );
  }

  const searchInput = ref<any | null>(null);

  function toggleSearch() {
    if (search.value === null) {
      search.value = '';
      nextTick(() => {
        searchInput.value?.focus?.();
      });
    } else {
      search.value = null;
    }
  }
</script>

<template>
  <div class="pos-order-menu">
    <div
      class="tabs-container"
      :class="{
        vertical: !isMediumDevice,
      }"
    >
      <div class="search-container">
        <v-btn
          v-if="search === null"
          color="primary"
          text
          block
          @click="toggleSearch"
        >
          <v-icon small>mdi-magnify</v-icon>
          <span v-if="!isMediumDevice">Buscar</span>
        </v-btn>
        <v-text-field
          v-else
          ref="searchInput"
          class="search-input"
          v-model="search"
          clearable
          dense
          outlined
          hide-details
          prepend-inner-icon="mdi-magnify"
          placeholder="Buscar"
          @click:clear="toggleSearch"
          @blur="search?.trim() === '' && toggleSearch()"
        />
      </div>
      <div v-show="search === null" class="tabs-inner">
        <v-tabs
          v-model="selectedTabIndex"
          :vertical="!isMediumDevice"
          class="tabs"
        >
          <v-tab v-for="tab in tabs" :key="tab.id" v-text="tab.label"></v-tab>
        </v-tabs>
      </div>
    </div>
    <div class="items-container">
      <template v-if="!hasCategories && !isSearching">
        <div class="empty-state">
          <v-icon large color="grey lighten-1">mdi-folder-open-outline</v-icon>
          <p class="empty-state-text">
            No hay categorías o productos disponibles
          </p>
          <p class="empty-state-subtext">
            Agregue categorías o productos desde Menú > Categorías o Menú >
            Items
          </p>
        </div>
      </template>
      <template v-else-if="!hasItems && isSearching">
        <div class="empty-state">
          <v-icon large color="grey lighten-1">mdi-magnify</v-icon>
          <p class="empty-state-text">No se encontraron productos</p>
          <p class="empty-state-subtext">
            Intente con otros términos de búsqueda
          </p>
        </div>
      </template>
      <template v-else-if="!hasItems && hasCategories">
        <div class="empty-state">
          <v-icon large color="grey lighten-1">mdi-package-variant</v-icon>
          <p class="empty-state-text">No hay productos en esta categoría</p>
          <p class="empty-state-subtext">
            Agregue productos desde Menú > Items
          </p>
        </div>
      </template>
      <template v-else>
        <ModalPosNewOrderMenuItem
          v-for="item in items"
          :key="item.id"
          :item="item"
          @click="onItemClick(item)"
        />
      </template>
    </div>
    <ModalPosNewOrderSummaryButton v-if="isSmallDevice" />
  </div>
</template>

<style scoped lang="scss">
  .pos-order-menu {
    @apply tw-flex tw-flex-col tw-h-full tw-relative tw-overflow-hidden;

    @screen lg {
      @apply tw-flex-row;
    }

    .tabs-container {
      --tabs-size: 160px;
      @apply tw-flex-none tw-bg-gray-50 tw-flex;

      ::v-deep(.v-slide-group__prev, .v-slide-group__next) {
        display: none !important;
      }

      .search-container {
        @apply tw-flex tw-items-center tw-justify-center;

        .search-input {
          @apply tw-bg-white tw-w-40;

          ::v-deep(.v-input__icon--clear) {
            opacity: 1 !important;
          }
        }
      }

      .tabs-inner {
        @apply tw-flex-1 tw-min-w-1 tw-overflow-x-auto;
      }

      &.vertical {
        @apply tw-flex tw-flex-col;
        width: var(--tabs-size);

        .search-container {
          @apply tw-mb-1;

          .search-input {
            @apply tw-w-full;
          }
        }

        .tabs-inner {
          @apply tw-flex-1 tw-overflow-x-hidden tw-min-h-1 tw-overflow-y-auto;

          ::v-deep(.v-tabs--vertical) {
            & > .v-tabs-bar .v-tab {
              @apply tw-whitespace-pre-line tw-bg-gray-50 tw-flex tw-line-clamp-2;
              height: 42px;
              max-width: var(--tabs-size);
            }
          }
        }
      }
    }

    .items-container {
      @apply tw-flex-1;
      @apply tw-grid tw-gap-2 tw-grid-cols-2 tw-p-3 tw-pb-20 tw-bg-gray-100 tw-overflow-y-auto tw-rounded-md tw-auto-rows-min;
      min-height: 10px;

      .empty-state {
        @apply tw-col-span-2 tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-center;

        .empty-state-text {
          @apply tw-text-gray-600 tw-font-medium tw-mt-4 tw-mb-2;
        }

        .empty-state-subtext {
          @apply tw-text-gray-500 tw-text-sm;
        }
      }
    }
  }
</style>
